import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../ui/StatusBadge';
import { 
  User, 
  Edit, 
  Calendar, 
  CheckSquare, 
  Users, 
  X 
} from 'lucide-react';

// Render tasks content
export const renderTasksContent = (tasks, users, currentUser, isPersonal, setSelectedContent, navigate) => {
  const filteredTasks = isPersonal 
    ? tasks.filter(t => 
      t.scope === 'personal' && 
      (t.assigneeId === currentUser.id || 
       t.createdBy === currentUser.id ||
       (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(currentUser.id)))
    )
  : tasks.filter(t => {
      // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
      if (t.isDailyRoutine || t.routineId || t.createdFrom === 'daily_routine') {
        return false;
      }
      
      // בדיקה נוספת - משימות שהשם שלהן מכיל תאריך (סימן למשימת שגרה)
      if (t.title && t.title.includes(' - ') && t.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
        return false;
      }
      
      // הסתר משימות אישיות של אחרים
      if (t.scope === 'personal' && t.assigneeId !== currentUser.id && t.createdBy !== currentUser.id) {
        return false;
      }
      
      // רק מנכ"ל וסמנכ"ל רואים משימות מכל המחלקות
      if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
        return t.scope === 'managerial' || t.scope === 'department';
      }
      
      // מנהל מחלקה - רק משימות של המחלקה שלו
      if (currentUser.role === 'dept_manager') {
        return (t.scope === 'managerial' || t.scope === 'department') && 
               t.departmentId === currentUser.departmentId;
      }
      
      // מנהל צוות - רק משימות של הצוות שלו
      if (currentUser.role === 'team_manager') {
        return (t.scope === 'managerial' || t.scope === 'department') && 
               (t.teamId === currentUser.teamId || 
                (t.departmentId === currentUser.departmentId && !t.teamId));
      }
      
      // עובד - רק משימות שהוא מוקצה אליהן או יצר
      return (t.assigneeId === currentUser.id || 
              (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(currentUser.id)) ||
              t.createdBy === currentUser.id);
    });
  const displayTasks = filteredTasks.slice(0, 6); // Show first 6
  
  return (
    <div className="mt-6 bg-white rounded-lg shadow">
      <div className="p-6 border-b flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            {isPersonal ? 'משימות אישיות' : 'משימות ניהוליות'}
          </h3>
          <p className="text-sm text-gray-600">
            {filteredTasks.length} משימות סה"כ
          </p>
        </div>
        <button
          onClick={() => setSelectedContent(null)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X size={20} />
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayTasks.map(task => {
            const assignee = users.find(u => u.id === task.assigneeId);
            const creator = users.find(u => u.id === (task.creatorId || task.createdBy));
            
            return (
              <div key={task.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                   onClick={() => navigate(`/tasks/${task.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{task.title}</h4>
                  <StatusBadge status={task.status || 'במסלול'} size="sm" />
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {task.description || 'אין תיאור'}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    {assignee && (
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {assignee.name}
                      </span>
                    )}
                    {creator && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <Edit size={12} />
                        {creator.name}
                      </span>
                    )}
                  </div>
                  {task.dueDate && (
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(task.dueDate).toLocaleDateString('he-IL')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {filteredTasks.length > 6 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate(isPersonal ? '/tasks?tab=personal' : '/tasks?tab=management')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              ראה עוד ({filteredTasks.length - 6} משימות נוספות)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Render projects content
export const renderProjectsContent = (projects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate) => {
  const filteredProjects = isPersonal 
    ? projects.filter(p => 
        p.scope === 'personal' && 
        (p.assigneeId === currentUser.id || 
         p.createdBy === currentUser.id ||
         (Array.isArray(p.teamMembers) && p.teamMembers.includes(currentUser.id)))
      )
    : projects.filter(p => {
        // הסתר פרויקטים אישיים של אחרים
        if (p.scope === 'personal' && p.assigneeId !== currentUser.id && p.createdBy !== currentUser.id) {
          return false;
        }
        
        // רק מנכ"ל וסמנכ"ל רואים פרויקטים מכל המחלקות
        if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
          return p.scope === 'managerial' || p.scope === 'department';
        }
        
        // מנהל מחלקה - רק פרויקטים של המחלקה שלו
        if (currentUser.role === 'dept_manager') {
          return (p.scope === 'managerial' || p.scope === 'department') && 
                 p.departmentId === currentUser.departmentId;
        }
        
        // מנהל צוות - רק פרויקטים של הצוות שלו
        if (currentUser.role === 'team_manager') {
          return (p.scope === 'managerial' || p.scope === 'department') && 
                 (p.teamId === currentUser.teamId || 
                  (p.departmentId === currentUser.departmentId && !p.teamId));
        }
        
        // עובד - רק פרויקטים שהוא מוקצה אליהם או יצר
        return (p.assigneeId === currentUser.id || 
                (Array.isArray(p.teamMembers) && p.teamMembers.includes(currentUser.id)) ||
                p.createdBy === currentUser.id);
      });
  
  const displayProjects = filteredProjects.slice(0, 6); // Show first 6
  
  return (
    <div className="mt-6 bg-white rounded-lg shadow">
      <div className="p-6 border-b flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            {isPersonal ? 'פרויקטים אישיים' : 'פרויקטים ניהוליים'}
          </h3>
          <p className="text-sm text-gray-600">
            {filteredProjects.length} פרויקטים סה"כ
          </p>
        </div>
        <button
          onClick={() => setSelectedContent(null)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X size={20} />
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayProjects.map(project => {
            const creator = users.find(u => u.id === (project.creatorId || project.createdBy));
            const projectTasks = tasks.filter(t => t.projectId === project.id);
            const completedTasks = projectTasks.filter(t => t.status === 'הושלם');
            const progress = projectTasks.length > 0 ? (completedTasks.length / projectTasks.length) * 100 : 0;
            
            return (
              <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                   onClick={() => navigate(`/projects/${project.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{project.name}</h4>
                  <StatusBadge status={project.status || 'במסלול'} size="sm" />
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {project.description || 'אין תיאור'}
                </p>
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span>התקדמות</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      <CheckSquare size={12} />
                      {projectTasks.length} משימות
                    </span>
                    {creator && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <Edit size={12} />
                        {creator.name}
                      </span>
                    )}
                  </div>
                  {project.endDate && (
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(project.endDate).toLocaleDateString('he-IL')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {filteredProjects.length > 6 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate(isPersonal ? '/projects?tab=personal' : '/projects?tab=management')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              ראה עוד ({filteredProjects.length - 6} פרויקטים נוספים)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Render teams content
export const renderTeamsContent = (teams, users, tasks, setSelectedContent, navigate, currentUser = null) => {
  // Filter teams by department if currentUser is provided and is dept_manager
  let filteredTeams = teams;
  if (currentUser && currentUser.role === 'dept_manager' && currentUser.departmentId) {
    filteredTeams = teams.filter(team => team.departmentId === currentUser.departmentId);
  }
  
  const displayTeams = filteredTeams.slice(0, 6); // Show first 6
  
  return (
    <div className="mt-6 bg-white rounded-lg shadow">
      <div className="p-6 border-b flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">צוותים</h3>
          <p className="text-sm text-gray-600">
            {filteredTeams.length} צוותים סה"כ
          </p>
        </div>
        <button
          onClick={() => setSelectedContent(null)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X size={20} />
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayTeams.map(team => {
            const teamMembers = users.filter(u => u.teamId === team.id);
            const teamTasks = tasks.filter(t => t.teamId === team.id);
            
            return (
              <div key={team.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                   onClick={() => navigate(`/teams/${team.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{team.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {team.isActive ? 'פעיל' : 'לא פעיל'}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {team.description || 'אין תיאור'}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users size={12} />
                    {teamMembers.length} חברים
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckSquare size={12} />
                    {teamTasks.length} משימות
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {teams.length > 6 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/teams')}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              ראה עוד ({teams.length - 6} צוותים נוספים)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Render departments content
export const renderDepartmentsContent = (departments, users, tasks, setSelectedContent, navigate, currentUser) => {
  // סנן מחלקות לפי תפקיד המשתמש
  const filteredDepartments = departments.filter(dept => {
    // מנהל מחלקה רואה רק את המחלקה שלו
    if (currentUser?.role === 'dept_manager') {
      return dept.id === currentUser.departmentId;
    }
    // מנכ"ל וסמנכ"ל רואים את כל המחלקות
    if (currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') {
      return true;
    }
    // תפקידים אחרים רואים את המחלקה שלהם
    return dept.id === currentUser?.departmentId;
  });
  
  const displayDepartments = filteredDepartments.slice(0, 6); // Show first 6
  
  return (
    <div className="mt-6 bg-white rounded-lg shadow">
      <div className="p-6 border-b flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">מחלקות</h3>
          <p className="text-sm text-gray-600">
            {filteredDepartments.length} מחלקות סה"כ
          </p>
        </div>
        <button
          onClick={() => setSelectedContent(null)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X size={20} />
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayDepartments.map(dept => {
            const deptUsers = users.filter(u => u.departmentId === dept.id);
            const deptTasks = tasks.filter(t => t.departmentId === dept.id);
            
            return (
              <div key={dept.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                   onClick={() => navigate(`/departments/${dept.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{dept.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    dept.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {dept.isActive ? 'פעיל' : 'לא פעיל'}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {dept.description || 'אין תיאור'}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-2">
                    <Users size={12} className="flex-shrink-0" />
                    <span>{deptUsers.length} עובדים</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <CheckSquare size={12} className="flex-shrink-0" />
                    <span>{deptTasks.length} משימות</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {filteredDepartments.length > 6 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/departments')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              ראה עוד ({departments.length - 6} מחלקות נוספות)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};